import { parsePhoneNumberFromString } from 'libphonenumber-js';
import supabase from '../../utils/supabase';
import { api } from '../../services/api';
import { normalizePhone } from '../../utils/phone';
import type { Lead, LeadWithPhones, LeadAssignment, CommentLogEntry } from './types';

export interface SearchHeuristic {
  kind: 'phone' | 'text';
  e164?: string;
  last9?: string;
  text?: string;
}

export function searchHeuristic(term: string): SearchHeuristic {
  const trimmed = term.trim();
  const digits = trimmed.replace(/\D/g, '');
  if (/[a-zA-Zא-ת]/.test(trimmed) || digits.length < 7) {
    return { kind: 'text', text: trimmed };
  }
  const parsed = parsePhoneNumberFromString(trimmed, 'IL');
  return {
    kind: 'phone',
    e164: parsed?.isValid() ? parsed.number : undefined,
    last9: digits.slice(-9),
    text: trimmed,
  };
}

export const LeadService = {
  async list(opts: { centerId?: string; status?: string; type?: string; sort?: string; sortDir?: string; search?: string } = {}): Promise<LeadWithPhones[]> {
    const params = new URLSearchParams();
    if (opts.centerId) params.set('center_id', opts.centerId);
    if (opts.status) params.set('status', opts.status);
    if (opts.type) params.set('type', opts.type);
    if (opts.sort) params.set('sort', opts.sort);
    if (opts.sortDir) params.set('sort_dir', opts.sortDir);
    if (opts.search) params.set('search', opts.search);
    const r = await api.get(`/crm/leads?${params}`);
    return r.data as LeadWithPhones[];
  },

  async update(id: string, patch: Partial<Pick<Lead, 'type' | 'status' | 'next_call_at' | 'internal_notes' | 'name' | 'email'>>): Promise<void> {
    const { error } = await supabase
      .from('crm_leads')
      .update({ ...patch, updated_at: new Date().toISOString() })
      .eq('id', id);
    if (error) throw error;
  },

  async create(centerId: string, input: { name: string; phones: { field_name: string; phone_raw: string }[]; email?: string; type?: string; status?: string; }): Promise<string> {
    const { data: lead, error } = await supabase
      .from('crm_leads')
      .insert({ center_id: centerId, source: 'local', name: input.name, email: input.email ?? null, type: input.type ?? null, status: input.status ?? null })
      .select('id')
      .single();
    if (error) throw error;
    const id = (lead as { id: string }).id;
    if (input.phones.length) {
      const rows = input.phones.map(p => {
        const n = normalizePhone(p.phone_raw);
        return { lead_id: id, center_id: centerId, field_name: p.field_name, phone_raw: p.phone_raw, phone_e164: n.e164, phone_last9: n.last9 };
      });
      await supabase.from('crm_lead_phones').insert(rows);
    }
    return id;
  },

  async remove(id: string): Promise<void> {
    const { error } = await supabase.from('crm_leads').delete().eq('id', id);
    if (error) throw error;
  },

  async assignments(leadId: string): Promise<LeadAssignment[]> {
    const { data, error } = await supabase.from('crm_lead_assignments').select('*').eq('lead_id', leadId);
    if (error) throw error;
    return (data ?? []) as LeadAssignment[];
  },

  async assign(leadId: string, centerId: string, repUserId: string, byUserId: string): Promise<void> {
    const { error } = await supabase
      .from('crm_lead_assignments')
      .upsert({ lead_id: leadId, rep_user_id: repUserId, center_id: centerId, assigned_by_user_id: byUserId });
    if (error) throw error;
  },

  async unassign(leadId: string, repUserId: string): Promise<void> {
    const { error } = await supabase
      .from('crm_lead_assignments')
      .delete()
      .eq('lead_id', leadId)
      .eq('rep_user_id', repUserId);
    if (error) throw error;
  },

  async commentLog(leadId: string): Promise<CommentLogEntry[]> {
    const { data, error } = await supabase
      .from('crm_comment_log')
      .select('*')
      .eq('lead_id', leadId)
      .order('created_at', { ascending: false })
      .limit(20);
    if (error) throw error;
    return (data ?? []) as CommentLogEntry[];
  },

  async pushComment(leadId: string, text: string): Promise<{ ok: boolean; fireberry_note_id?: string }> {
    const r = await api.post(`/crm/leads/${leadId}/push-comment`, { text });
    return r.data;
  },

  async lookupLive(phone: string, callId?: string, centerId?: string): Promise<{
    found: boolean;
    lead?: LeadWithPhones;
    source?: 'local' | 'fireberry' | 'none';
    duration_ms: number;
  }> {
    const r = await api.post('/crm/lookup-live', { phone, call_id: callId, center_id: centerId });
    return r.data;
  },
};
